import { defineEndpoint, PageQuery, SearchQuery, SearchResult } from '@itc/shared';
import { z } from 'zod';
import {
  AddStockInput,
  AdjustInput,
  ArchiveInput,
  Item,
  ItemInput,
  ItemUpdate,
  Lot,
  LotUpdate,
  MoveInput,
  Movement,
  People,
  Person,
  PrepLevel,
  PrepLevelInput,
  RecordId,
  RestockInput,
  RestockPlan,
  Ship,
  ShipInput,
  ShipStatus,
  ShipUpdate,
  StockView,
} from './schemas.ts';
import { Job, JobInput, JobUpdate, PickupInput, RefineryView } from './refinery.ts';

// Supply's contracts (design §7.3, plan 2B). Every change takes an Idempotency-Key,
// so a retried request never adds stock twice.

const ById = z.object({ id: RecordId });

/** Lots changed by a stock change, with their movements. */
const StockResult = z.object({ lots: z.array(Lot), movements: z.array(Movement), people: People });

/** The stockpile, every lot aboard ships, the items and the prep levels. Empty lots are included for "Show empty". */
/** R15: what the caller may find. Open to any login, and empty without supply.view. */
export const searchSupply = defineEndpoint({ method: 'GET', path: '/supply/search', permission: 'authenticated', query: SearchQuery, response: SearchResult });

export const getStock = defineEndpoint({
  method: 'GET',
  path: '/supply/stock',
  permission: 'supply.view',
  response: StockView,
});

/** The movements log, newest first: all of it, or one lot's or one item's. */
export const listMovements = defineEndpoint({
  method: 'GET',
  path: '/supply/movements',
  permission: 'supply.view',
  query: PageQuery.extend({ lot: RecordId.optional(), item: RecordId.optional() }),
  response: z.object({ items: z.array(Movement), nextCursor: z.string().nullable(), people: People }),
});

/** Every item, inactive ones included. */
export const listItems = defineEndpoint({
  method: 'GET',
  path: '/supply/items',
  permission: 'supply.view',
  response: z.object({ items: z.array(Item) }),
});

export const createItem = defineEndpoint({
  method: 'POST',
  path: '/supply/items',
  permission: 'supply.manage',
  idempotencyKey: 'required',
  body: ItemInput,
  response: Item,
});

/** Renames or re-describes an item. Its category and unit change only while it has no lots. */
export const updateItem = defineEndpoint({
  method: 'PUT',
  path: '/supply/items/:id',
  permission: 'supply.manage',
  idempotencyKey: 'required',
  params: ById,
  body: ItemUpdate,
  response: Item,
});

/** Only when no lot of the item holds anything. */
export const deactivateItem = defineEndpoint({
  method: 'POST',
  path: '/supply/items/:id/deactivate',
  permission: 'supply.admin',
  idempotencyKey: 'required',
  params: ById,
  response: Item,
});

/** Adds stock of one item at one place, stacking each line onto its lot (R3), and optionally sets the prep level there. */
export const addStock = defineEndpoint({
  method: 'POST',
  path: '/supply/lots',
  permission: 'supply.manage',
  idempotencyKey: 'required',
  body: AddStockInput,
  response: StockResult.extend({ prepLevel: PrepLevel.nullable() }),
});

/** Changes a lot's details. Changing what stacks it onto a lot that already exists is refused: move it instead. */
export const updateLot = defineEndpoint({
  method: 'PUT',
  path: '/supply/lots/:id',
  permission: 'supply.manage',
  idempotencyKey: 'required',
  params: ById,
  body: LotUpdate,
  response: z.object({ lot: Lot, people: People }),
});

/** The stepper: adds a (signed) delta to a lot's amount. Never below zero. */
export const adjustLot = defineEndpoint({
  method: 'POST',
  path: '/supply/lots/:id/adjust',
  permission: 'supply.manage',
  idempotencyKey: 'required',
  params: ById,
  body: AdjustInput,
  response: StockResult,
});

/** Sets an item's prep level at a place; 0 clears it. */
export const setPrepLevel = defineEndpoint({
  method: 'PUT',
  path: '/supply/prep-levels',
  permission: 'supply.manage',
  idempotencyKey: 'required',
  body: PrepLevelInput,
  response: PrepLevel,
});

/** Active members matching `q`, for the holder and refiner pickers. */
export const searchPeople = defineEndpoint({
  method: 'GET',
  path: '/supply/people',
  permission: 'supply.manage',
  query: z.object({ q: z.string().trim().max(64).default('') }),
  response: z.object({ people: z.array(Person) }),
});

const ShipResult = z.object({ ship: Ship, status: ShipStatus.nullable(), people: People });

/** Every ship, archived ones included, with each active ship's readiness (R8), and whose ships list first (R9). */
export const listShips = defineEndpoint({
  method: 'GET',
  path: '/supply/ships',
  permission: 'supply.view',
  response: z.object({ ships: z.array(Ship), statuses: z.array(ShipStatus), people: People, pinnedOwner: z.string() }),
});

export const createShip = defineEndpoint({
  method: 'POST',
  path: '/supply/ships',
  permission: 'supply.manage',
  idempotencyKey: 'required',
  body: ShipInput,
  response: ShipResult,
});

/** Edits a ship; a rename keeps everything aboard. */
export const updateShip = defineEndpoint({
  method: 'PUT',
  path: '/supply/ships/:id',
  permission: 'supply.manage',
  idempotencyKey: 'required',
  params: ById,
  body: ShipUpdate,
  response: ShipResult,
});

/** "Mark restocked": sets the last restocked time without moving anything. */
export const markRestocked = defineEndpoint({
  method: 'POST',
  path: '/supply/ships/:id/restocked',
  permission: 'supply.manage',
  idempotencyKey: 'required',
  params: ById,
  response: ShipResult,
});

/** Archives a ship, moving what's aboard to a stockpile location. */
export const archiveShip = defineEndpoint({
  method: 'POST',
  path: '/supply/ships/:id/archive',
  permission: 'supply.manage',
  idempotencyKey: 'required',
  params: ById,
  body: ArchiveInput,
  response: z.object({ ship: Ship, moved: StockResult }),
});

/** Moves part or all of a lot to another place (R7). */
export const moveLot = defineEndpoint({
  method: 'POST',
  path: '/supply/lots/:id/move',
  permission: 'supply.manage',
  idempotencyKey: 'required',
  params: ById,
  body: MoveInput,
  response: StockResult,
});

/** What a restock would move now: all short prep levels, or one item's. */
export const previewRestock = defineEndpoint({
  method: 'GET',
  path: '/supply/ships/:id/restock',
  permission: 'supply.manage',
  params: ById,
  query: z.object({ item: RecordId.optional() }),
  response: z.object({ plan: RestockPlan, fingerprint: z.string() }),
});

/** Restocks from a fresh plan; `changed` says whether it differs from the preview's. */
export const restockShip = defineEndpoint({
  method: 'POST',
  path: '/supply/ships/:id/restock',
  permission: 'supply.manage',
  idempotencyKey: 'required',
  params: ById,
  body: RestockInput,
  response: StockResult.extend({ plan: RestockPlan, changed: z.boolean() }),
});

// Refinery jobs (plan 2B Chunk 5). Every route is open to any logged-in member, who
// may keep personal jobs; the handlers check org jobs' permissions, and a personal
// job is its refiner's alone: anyone else gets 404.

const JobResult = z.object({ job: Job, people: People });

/** Your jobs, and the org's for supply.refine and supply.view: open ones and recent pickups, or all with `older`. */
export const getRefinery = defineEndpoint({
  method: 'GET',
  path: '/supply/refinery',
  permission: 'authenticated',
  query: z.object({ older: z.stringbool().default(false) }),
  response: RefineryView,
});

/** Active materials, for the job sheet's names. */
export const listMaterials = defineEndpoint({
  method: 'GET',
  path: '/supply/refinery/materials',
  permission: 'authenticated',
  response: z.object({ materials: z.array(z.object({ id: z.string(), name: z.string() })) }),
});

export const getJob = defineEndpoint({
  method: 'GET',
  path: '/supply/refinery/:id',
  permission: 'authenticated',
  params: ById,
  response: JobResult,
});

/** Starts a job. An org job's ore with a source lot leaves stock now. */
export const createJob = defineEndpoint({
  method: 'POST',
  path: '/supply/refinery',
  permission: 'authenticated',
  idempotencyKey: 'required',
  body: JobInput,
  response: JobResult,
});

/** Edits a job's lines, time left, refinery and method, giving back the old ore and taking the new. */
export const updateJob = defineEndpoint({
  method: 'PUT',
  path: '/supply/refinery/:id',
  permission: 'authenticated',
  idempotencyKey: 'required',
  params: ById,
  body: JobUpdate,
  response: JobResult,
});

/** 15 more minutes. */
export const extendJob = defineEndpoint({
  method: 'POST',
  path: '/supply/refinery/:id/extend',
  permission: 'authenticated',
  idempotencyKey: 'required',
  params: ById,
  response: JobResult,
});

/** Ready now, with no notification. */
export const markJobReady = defineEndpoint({
  method: 'POST',
  path: '/supply/refinery/:id/ready',
  permission: 'authenticated',
  idempotencyKey: 'required',
  params: ById,
  response: JobResult,
});

/** Records what came out; an org job's output goes to the stockpile. */
export const pickupJob = defineEndpoint({
  method: 'POST',
  path: '/supply/refinery/:id/pickup',
  permission: 'authenticated',
  idempotencyKey: 'required',
  params: ById,
  body: PickupInput,
  response: JobResult,
});

/** Cancels a job before pickup; an org job's ore goes back. */
export const cancelJob = defineEndpoint({
  method: 'POST',
  path: '/supply/refinery/:id/cancel',
  permission: 'authenticated',
  idempotencyKey: 'required',
  params: ById,
  response: JobResult,
});

export const deletePreset = defineEndpoint({
  method: 'POST',
  path: '/supply/refinery/presets/:id/delete',
  permission: 'supply.admin',
  idempotencyKey: 'required',
  params: ById,
  response: z.object({ ok: z.literal(true) }),
});

/** The nav badges' counts (R18): low stockpile prep levels, and stocked ships that need a restock. */
export const getCounts = defineEndpoint({
  method: 'GET',
  path: '/supply/counts',
  permission: 'supply.view',
  response: z.object({ low: z.number().int(), short: z.number().int() }),
});
